import React, { useContext, useEffect, useState, useMemo } from "react"
import dayjs, { Dayjs } from "dayjs"
import axios from "axios"
import { useTranslation } from "react-i18next"
import { useResponsive } from "src/utils/useResponsive"
import { useLocalStorage } from "src/utils/useLocalStorage"
import { useDebounce } from "src/utils/useDebounce"

import PageContainer from "src/components/page/PageContainer"
import { Grid, Stack, Dialog, DialogContent, DialogActions, Typography, Button } from "@mui/material"
import { JobInfoDrawer } from "./drawer/JobInfoDrawer"
import { MultilangJob, MultiLangJobStatus } from "src/services/api/document"
import MyFilesGrid from "./MyFilesGrid"
import MyFilesTable from "./MyFilesTable"
import MyFilesMobile from "./MyFilesMobile"
import MyFilesFilter, { StatusOption, RequesterRecord, getRequesterName } from "./MyFilesFilter"
import { UserContext } from "src/providers"

const formatDate = (date: Dayjs | null) => {
  if (!date) return undefined
  return date.format("YYYY-MM-DD")
}

const statusesForList = ["QUOTED", "IN_PROGRESS", "FINISHED", "CANCELLED", "REJECTED"]
const statusesForGrid = statusesForList.filter((status) => status !== "CANCELLED" && status !== "REJECTED")

const MyFilesPage = () => {
  const { t } = useTranslation("common")
  const { user } = useContext(UserContext)
  const { isMobile } = useResponsive()
  const [multilangJob, setMultilangJob] = useState<MultilangJob>()
  const [updatedJob, setUpdatedJob] = useState<MultilangJob>()
  const [viewType, setViewType] = useLocalStorage<"grid" | "list">("myfiles.viewType", "grid")
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const statuses = viewType === "grid" ? statusesForGrid : statusesForList
  const statusOptions: StatusOption[] = statuses.map((status) => ({ label: t(`files.${status}`), value: status }))
  const [statusFilter, setStatusFilter] = useLocalStorage<StatusOption[]>("myfiles.statusFilter", [])
  useEffect(() => {
    setStatusFilter((prev) => prev.filter((status) => statuses.includes(status.value)))
  }, [viewType])

  const [requesterList, setRequesterList] = useState<RequesterRecord[]>()
  const [requesterFilter, setRequesterFilter] = useLocalStorage<RequesterRecord | undefined>(
    "myfiles.requesterFilter",
    undefined
  )
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  const [uploadDateFrom, setUploadDateFrom] = useState<Dayjs | null>(null)
  const [uploadDateTo, setUploadDateTo] = useState<Dayjs | null>(null)

  const filters = useMemo(
    () => ({
      searchQuery: debouncedSearchQuery,
      uploadDateFrom: formatDate(uploadDateFrom),
      uploadDateTo: formatDate(uploadDateTo),
      statuses:
        statusFilter.length > 0
          ? statusFilter.map((status) => status.value as MultiLangJobStatus).join(",")
          : undefined,
      requesterId: requesterFilter?.userId
    }),
    [debouncedSearchQuery, uploadDateFrom, uploadDateTo, statusFilter, requesterFilter]
  )

  const getCompanyUsers = (companyId: string) =>
    axios.get<RequesterRecord[]>(`/api/wfl/groups_management/companies/${companyId}/listUsers`, {}).then((r) => r.data)

  const sortByFirstName = (a: RequesterRecord, b: RequesterRecord) => {
    return a.firstName.localeCompare(b.firstName)
  }

  useEffect(() => {
    user &&
      user.company.profile === "ORGANIZATION_ADMIN" &&
      getCompanyUsers(user.company.companyId)
        .then((possibleExecutors) =>
          possibleExecutors.filter((e) => e.roles.includes("ROLE_BUYER")).sort((a, b) => sortByFirstName(a, b))
        )
        .then((sortedExecutors) => {
          setRequesterList(sortedExecutors)
        })
  }, [])

  const clearFilters = () => {
    setStatusFilter([])
    setUploadDateFrom(null)
    setUploadDateTo(null)
    setSearchQuery("")
  }

  const handleRequesterFilterChange = (requester: RequesterRecord | undefined) => {
    setRequesterFilter(requester)
  }

  const handleOpenEmailModal = () => {
    setIsEmailModalOpen(true)
  }

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false)
  }

  return (
    <PageContainer>
      <Stack sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {multilangJob && (
          <JobInfoDrawer
            multilangJob={multilangJob}
            setMultilangJob={setMultilangJob}
            onJobUpdated={setUpdatedJob}
          />
        )}
        <MyFilesFilter
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          statusOptions={statusOptions}
          uploadDateFrom={uploadDateFrom}
          onUploadDateFromChange={setUploadDateFrom}
          uploadDateTo={uploadDateTo}
          onUploadDateToChange={setUploadDateTo}
          requesterList={requesterList}
          requesterFilter={requesterFilter}
          onRequesterFilterChange={handleRequesterFilterChange}
          onClearFilters={clearFilters}
          viewType={viewType}
          onViewTypeChange={setViewType}
        />

        {viewType === "grid" ? (
          isMobile ? (
            <MyFilesMobile
              setMultilangJob={setMultilangJob}
              filters={filters}
              onOpenEmailModal={handleOpenEmailModal}
              updatedJob={updatedJob}
            />
          ) : (
            <MyFilesGrid
              setMultilangJob={setMultilangJob}
              filters={filters}
              onOpenEmailModal={handleOpenEmailModal}
              updatedJob={updatedJob}
            />
          )
        ) : (
          <MyFilesTable
            setMultilangJob={setMultilangJob}
            filters={filters}
            requesterList={requesterList}
            onOpenEmailModal={handleOpenEmailModal}
            updatedJob={updatedJob}
          />
        )}

        {/* Email notification modal */}
        <Dialog
          open={isEmailModalOpen}
          onClose={handleCloseEmailModal}
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: "#AFEFFF",
              p: 2
            }
          }}
        >
          <DialogContent sx={{ textAlign: "center", pb: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: "bold", mb: 2 }}>
              {"Check your email"}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {"We've sent you an email with a link to open the editor on your desktop."}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: "column", gap: 2, px: 3, pb: 3 }}>
            <Button
              onClick={handleCloseEmailModal}
              variant="contained"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </PageContainer>
  )
}

export default MyFilesPage
